<template>
  <article class="base-modal">
    <PulseLoader />
    <h1 class="loadding__title">{{ t('general.wait') }}</h1>
  </article>
</template>

<script lang="ts" setup>
  import * as Android from '@/api/android';
  import PaymentApprovalRequest from '@/api/android/interfaces/PaymentApprovalRequest';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import { logger } from '@/logger';
  import { useCommonStore } from '@/stores/common';
  import { useModalStore } from '@/stores/modal';
  import { usePaymentStore } from '@/stores/payment';
  import { onBeforeUnmount, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    /** 카드 결제 요청 금액. 값이 없는 경우 남은 금액 전체 결제 */
    amount?: number;
  }>();

  const { t } = useI18n();
  const modalStore = useModalStore();
  const commonStore = useCommonStore();
  const paymentStore = usePaymentStore();

  const { createPaymentSplit } = usePayment();

  const paymentSplit = createPaymentSplit(PaymentMethod.Card, props.amount);

  onMounted(async () => {
    logger.info(`[PaymentEasyModal] 간편 결제 요청. Request - ${JSON.stringify(paymentSplit)}`);

    const loadingId = commonStore.showLoading();
    try {
      // 결제 중에는 대기 팝업 타임아웃 해제
      commonStore.clearWaitingPopupInterval();
      // 결제 중에는 실시간 데이터 업데이트 인터벌 해제
      commonStore.clearRealtimeUpdateInterval();

      // 카드 승인 요청
      await approveEasyPay();
    } catch (error) {
      logger.info(`[PaymentCardModal] 간편 결제 진행 중 에러 발생. ${JSON.stringify((error as Error).message)}`);
      modalStore.rejectSyncModal(error);
    } finally {
      commonStore.hideLoading(loadingId);
    }
  });

  onBeforeUnmount(() => {
    // 대기 팝업 타임아웃 설정
    commonStore.setWaitingPopupInterval();
  });

  /**
   * 카드 승인 요청 콜백
   * @param response 카드 승인 성공 여부
   */
  const approveEasyPay = async () => {
    if (!(window as any).AndroidBridge.approvalEasyPay) {
      await commonStore.showDialog({ type: 'alert', message: t('error.app_update') });
      modalStore.resolveSyncModal<boolean>(false);
      return;
    }

    // 승인 요청
    const response = await Android.approvalEasyPayAsync({
      Ininstallment: paymentSplit.installment,
      PaymentAmount: paymentSplit.amount,
      VATAmount: paymentSplit.tax,
      FreeAmount: paymentSplit.taxFree,
    } satisfies PaymentApprovalRequest);

    // 승인 요청 성공인 경우
    if (response.RET_CODE === 0) {
      logger.info(`[PaymentEasyModal] 간편결제 결제 성공. Response - ${JSON.stringify(response)}`);

      // 결제 성공한 경우 승인 정보 세팅
      paymentSplit.paymentApproval = response;

      // 분할 결제 데이터에 추가
      paymentStore.addPaymentSplit(paymentSplit);

      // 성공 반환
      modalStore.resolveSyncModal<boolean>(true);
      return;
    }

    // 시간 초과 또는 취소 버튼을 누른 경우 뒤로가기
    if (response.RET_CODE === -1) {
      logger.info(`[PaymentEasyModal] 간편결제 결제 시간 초과 또는 취소 버튼. Response - ${JSON.stringify(response)}`);
      modalStore.resolveSyncModal<boolean>(false);
      return;
    }

    // 기타 에러 발생 시 경고 메시지 출력 후 취소
    logger.info(`[PaymentEasyModal] 간편결제 결제 기타 에러. Response - ${JSON.stringify(response)}`);
    await commonStore.showDialog({ type: 'alert', message: response.RET_MSG });
    modalStore.resolveSyncModal<boolean>(false);
    return;
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .payment-card {
    position: fixed;
    top: 0;
    left: 0;
    z-index: vars.$z-index-sync-modal;
    width: 100vw;
    height: 100vh;
    background-color: #fff;

    .payment-card__container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 100vh;
      .payment-card__guide {
        text-align: center;
        h1.guide__title {
          font-size: 3.7313vw;
          font-weight: 700;
          margin-bottom: 1.3433vw;
          @media (max-width: vars.$md) {
            font-size: 18px;
          }
          span {
            color: var(--point-color-secondary);
          }
        }
        .guide__description1 {
          font-size: 2.0896vw;
          margin-bottom: 0.8955vw;
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
        .guide__description2 {
          font-size: 2.0896vw;
          color: var(--text-secondary);
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
      }
      .payment-card__figure {
        max-width: 36.194vw;
        margin: 0 auto;
        @media (max-width: vars.$md) {
          max-width: 200px;
        }
        img {
          width: 100%;
        }
      }
    }
  }
</style>
